import { UserManager, UserManagerSettings, User as OidcUser } from 'oidc-client-ts';
import { User } from '../types';
import { graphService } from './graph';

interface AuthConfig {
  authority: string;
  client_id: string;
  redirect_uri: string;
  post_logout_redirect_uri: string;
  response_type: string;
  scope: string;
}

class AuthService {
  private userManager: UserManager;

  constructor(config: AuthConfig) {
    const settings: UserManagerSettings = {
      ...config,
      automaticSilentRenew: true, // Enable but control it carefully
      silent_redirect_uri: `${import.meta.env.VITE_APP_BASE_URL || window.location.origin}/silent-callback`,
      filterProtocolClaims: true,
      loadUserInfo: false, // Disable to prevent additional calls
      response_mode: 'query',
      monitorSession: false, // Disable session monitoring to prevent check session iframe issues
      accessTokenExpiringNotificationTimeInSeconds: 300, // Notify 5 minutes before expiry
      silentRequestTimeoutInSeconds: 30, // Timeout for silent requests
    };

    this.userManager = new UserManager(settings);

    // Handle events
    this.userManager.events.addUserLoaded((user) => {
      console.log('User loaded/renewed successfully');
    });

    this.userManager.events.addSilentRenewError((error) => {
      console.error('Silent token renewal failed:', error);
      // Don't automatically sign out on renewal failure
      // Let the app handle expired tokens gracefully
    });

    this.userManager.events.addUserSignedOut(() => {
      console.log('User signed out event triggered');
      // Clear storage and reload
      this.userManager.clearStaleState();
      window.location.href = '/';
    });

    this.userManager.events.addAccessTokenExpiring(() => {
      console.log('Access token expiring soon');
      // Don't attempt automatic renewal - let the app handle it
    });
  }

  async getUser(): Promise<User | null> {
    try {
      const oidcUser = await this.userManager.getUser();
      if (!oidcUser || oidcUser.expired) {
        return null;
      }

      // Profile picture from OIDC (if available)
      const picture = oidcUser.profile.picture;

      return {
        id: oidcUser.profile.sub,
        email: oidcUser.profile.email || '',
        name: oidcUser.profile.name || oidcUser.profile.given_name || oidcUser.profile.preferred_username || (oidcUser.profile.email || '').split('@')[0],
        picture,
        orgId: oidcUser.profile.org_id,
        roles: oidcUser.profile.roles || [],
        permissions: oidcUser.profile.permissions || [],
      };
    } catch (error) {
      console.error('Error getting user:', error);
      return null;
    }
  }

  async getToken(): Promise<string | null> {
    try {
      const user = await this.userManager.getUser();
      if (!user) {
        console.log('No user found in token storage');
        return null;
      }
      
      if (user.expired) {
        console.log('Token appears expired, checking if refresh is possible...');
        // Try silent renewal once before giving up
        try {
          const renewedUser = await this.userManager.signinSilent();
          if (renewedUser && !renewedUser.expired) {
            console.log('Token successfully renewed');
            return renewedUser.access_token;
          }
        } catch (error) {
          console.log('Silent renewal failed, token is truly expired');
          // Only clear if silent renewal actually fails
          await this.userManager.removeUser();
          return null;
        }
      }
      
      return user.access_token;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }

  async signIn(): Promise<void> {
    try {
      await this.userManager.signinRedirect();
    } catch (error) {
      console.error('Sign in failed:', error);
      throw error;
    }
  }

  async signOut(): Promise<void> {
    try {
      // For Azure AD, often the logout URL is too long, so do local logout first
      await this.userManager.removeUser();
      
      // Create the correct Azure AD v2.0 logout URL
      const authority = this.userManager.settings.authority;
      // Remove /v2.0 from authority if present, then add the correct logout path
      const baseAuthority = authority?.replace(/\/v2\.0\/?$/, '');
      const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
      const logoutUrl = `${baseAuthority}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(baseUrl)}`;
      
      // Redirect to Azure AD logout
      window.location.href = logoutUrl;
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback: just clear local session and redirect
      await this.userManager.removeUser();
      window.location.href = '/';
    }
  }

  async handleSignInCallback(): Promise<User | null> {
    try {
      const oidcUser = await this.userManager.signinRedirectCallback();
      
      // Profile picture from OIDC (if available)
      const picture = oidcUser.profile.picture;
      
      const user = {
        id: oidcUser.profile.sub,
        email: oidcUser.profile.email || '',
        name: oidcUser.profile.name,
        picture,
        orgId: oidcUser.profile.org_id,
        roles: oidcUser.profile.roles || [],
        permissions: oidcUser.profile.permissions || [],
      };
      
      return user;
    } catch (error) {
      console.error('AuthService: Sign in callback error:', error);
      console.error('AuthService: Error details:', {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        toString: error?.toString()
      });
      if (error instanceof Error) {
        console.error('AuthService: Error name:', error.name);
        console.error('AuthService: Error message:', error.message);
      }
      return null;
    }
  }

  async silentRenew(): Promise<void> {
    await this.userManager.signinSilent();
  }
}


// Create auth service instance with configuration
// Use stable base URL for production to avoid changing redirect URIs with each deployment
const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;

const authConfig: AuthConfig = {
  authority: import.meta.env.VITE_OIDC_AUTHORITY || 'https://login.microsoftonline.com/4dd30c56-6534-42ea-8e25-03214368f83d/v2.0',
  client_id: import.meta.env.VITE_OIDC_CLIENT_ID || 'b7e61261-4391-4018-9a90-1370de3045ee',
  redirect_uri: `${baseUrl}/callback`,
  post_logout_redirect_uri: baseUrl,
  response_type: 'code',
  scope: import.meta.env.VITE_OIDC_SCOPE || 'openid profile email https://graph.microsoft.com/User.Read https://graph.microsoft.com/Group.Read.All',
};

export const authService = new AuthService(authConfig);
export default authService;